/**
 * RunTestForm schema
 *
 * Single source of truth for the run-test Sheet form. Drives react-hook-form
 * defaults, FormMessage validation, and the submit payload. Lives alongside
 * ExecutionDialog.tsx (now a Sheet) and the section components.
 *
 * See plans/test-creation-redesign.md for the design intent.
 *
 * Why flat z.object + superRefine instead of z.discriminatedUnion:
 *   z.discriminatedUnion options don't accept `.refine()` chaining and they
 *   make `form.watch()` return `never` until TypeScript narrows the union,
 *   which fights React Hook Form's setValue/watch API. Flat object with
 *   conditional validation in `superRefine` plays much better with the
 *   form and keeps the same UX contract.
 */

import { z } from 'zod';
import type { TestConfiguration, ForcedRat } from '@simcheck/shared-types';

// 'pass_windows' (#252): fire at each uploaded satellite pass window's start
// (+ offsetSec) for the pinned modem — only meaningful with a modem pin.
const RecurrenceUnitSchema = z.enum(['minutes', 'hours', 'days', 'weeks', 'months', 'pass_windows']);

const RecurrenceSchema = z.object({
  interval: z.coerce.number().int().positive(),
  unit: RecurrenceUnitSchema,
  offsetSec: z.coerce.number().int().min(0).optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  cronExpression: z.string().optional(),
});

const FORCED_RATS = ['auto', 'nr5g', 'nr5g_nsa', 'lte', 'lte_m', 'nb_iot', 'wcdma', 'gsm'] as const;
const ForcedRatSchema = z.enum(FORCED_RATS);

// ── Main schema ───────────────────────────────────────────────────────────

export const RunTestFormSchema = z
  .object({
    configMode: z.enum(['oneOff', 'useSaved']),

    // Saved-config path
    testConfigurationId: z.string().optional(),

    // Flow (required regardless of mode; useSaved infers from saved config)
    flowId: z.string(),

    // Where
    locationMode: z.enum(['single', 'cluster']).default('single'),
    locationId: z.string().optional(),
    clusterId: z.string().optional(),
    // Modem pin (#252): run on exactly this device/modem at the location
    // (persistent-attach / PLMN-hunt modems). Threaded to the allocator via
    // locationRequirements[0].deviceId / modemImei.
    pinDeviceId: z.string().optional(),
    pinModemImei: z.string().optional(),

    // eSIM
    esimMode: z.enum(['auto', 'single', 'batch', 'none']).default('auto'),
    esimProfileId: z.string().optional(),
    esimGroupId: z.string().optional(),
    // #375: a limited-download eSIM may move between modems (uses a download).
    esimAllowRelocation: z.boolean().default(false),

    // Network (optional)
    forcedRat: ForcedRatSchema.default('auto'),
    forcedNetworkMccMnc: z.string().optional(),

    // Flow input parameter values (apn, dialNumber, target, etc.), keyed by the
    // selected flow's input names. Empty/omitted → flow defaults.
    flowInputs: z.record(z.any()).optional(),

    // Timing
    timing: z.enum(['now', 'scheduled', 'recurring']).default('now'),
    executeAt: z.date().optional(),
    recurrence: RecurrenceSchema.optional(),
    // IANA time zone (e.g. "America/New_York", "Asia/Jerusalem") used by
    // BOTH Scheduled (interprets executeAt's wall-clock H:M:D) and Recurring
    // (interprets recurrence.startDate's wall-clock H:M:D + DOW/DOM). The
    // backend cron resolves the actual UTC fire instant by re-reading this
    // pair. Default is the browser's resolved zone.
    timeZone: z.string().optional(),

    // Save-as (one-off mode only)
    saveAs: z.boolean().default(false),
    saveAsName: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.configMode === 'useSaved') {
      if (!v.testConfigurationId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['testConfigurationId'],
          message: 'Pick a saved configuration',
        });
      }
      return; // saved-config path bypasses the rest of the validations
    }

    // oneOff path
    if (!v.flowId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['flowId'], message: 'Pick a flow' });
    }
    if (v.locationMode === 'single' && !v.locationId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['locationId'], message: 'Pick a location' });
    }
    if (v.locationMode === 'cluster' && !v.clusterId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['clusterId'], message: 'Pick a cluster' });
    }
    if (v.esimMode === 'single' && !v.esimProfileId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['esimProfileId'], message: 'Pick an eSIM profile' });
    }
    if (v.esimMode === 'batch' && !v.esimGroupId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['esimGroupId'], message: 'Pick an eSIM group' });
    }
    if (v.timing === 'scheduled') {
      if (!v.executeAt || v.executeAt.getTime() <= Date.now()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['executeAt'], message: 'Pick a date in the future' });
      }
    }
    if (v.timing === 'recurring') {
      if (!v.recurrence || v.recurrence.interval <= 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recurrence', 'interval'], message: 'Set an interval' });
      }
    }
    if (v.saveAs && (!v.saveAsName || v.saveAsName.trim().length < 3)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['saveAsName'], message: 'Name (min 3 chars) required to save' });
    }
  });

export type RunTestFormValues = z.infer<typeof RunTestFormSchema>;

// ── Defaults helpers ──────────────────────────────────────────────────────

export function defaultsForCreate(): RunTestFormValues {
  return {
    configMode: 'oneOff',
    testConfigurationId: undefined,
    flowId: '',
    locationMode: 'single',
    locationId: undefined,
    clusterId: undefined,
    pinDeviceId: undefined,
    pinModemImei: undefined,
    esimMode: 'auto',
    esimProfileId: undefined,
    esimGroupId: undefined,
    esimAllowRelocation: false,
    forcedRat: 'auto',
    forcedNetworkMccMnc: undefined,
    timing: 'now',
    executeAt: undefined,
    recurrence: undefined,
    timeZone: (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined) || undefined,
    saveAs: false,
    saveAsName: undefined,
  };
}

export function defaultsForUseSaved(testConfigurationId: string, flowId: string): RunTestFormValues {
  return {
    ...defaultsForCreate(),
    configMode: 'useSaved',
    testConfigurationId,
    flowId,
  };
}

/**
 * Hydrate the form from an existing TestConfiguration. Used by Edit mode
 * and by "Use saved" → "fork to one-off" cases.
 */
export function defaultsFromConfig(c: TestConfiguration): RunTestFormValues {
  const locReq = c.locationRequirements?.[0];
  const isCluster = !!locReq?.clusterId;
  const timing = c.timing?.type === 'scheduled' ? 'scheduled'
                : c.timing?.type === 'recurring' ? 'recurring'
                : 'now';

  return {
    configMode: 'oneOff',
    testConfigurationId: c.id, // tracked so edit-mode can update in place
    flowId: c.flowId ?? '',
    locationMode: isCluster ? 'cluster' : 'single',
    locationId: locReq?.locationId,
    clusterId: locReq?.clusterId,
    pinDeviceId: locReq?.deviceId,
    pinModemImei: locReq?.modemImei,
    esimMode: (c.esim?.mode ?? 'auto') as 'auto' | 'single' | 'batch' | 'none',
    esimProfileId: c.esim?.profileId,
    esimGroupId: c.esim?.batch?.groupId,
    esimAllowRelocation: c.esim?.allowRelocation === true,
    forcedRat: (((c as any).forcedRat as ForcedRat) ?? 'auto'),
    forcedNetworkMccMnc: (c as any).forcedNetworkMccMnc,
    timing,
    executeAt: c.timing?.executeAt instanceof Date
      ? c.timing.executeAt
      : (c.timing?.executeAt ? new Date(c.timing.executeAt) : undefined),
    recurrence: c.timing?.recurrence
      ? {
          interval: c.timing.recurrence.interval,
          unit: c.timing.recurrence.unit,
          offsetSec: (c.timing.recurrence as any).offsetSec,
          startDate: c.timing.recurrence.startDate as Date | undefined,
          endDate: c.timing.recurrence.endDate as Date | undefined,
          cronExpression: c.timing.recurrence.cronExpression,
        }
      : undefined,
    timeZone: (c as any).timeZone || (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined) || undefined,
    // Load the config's SAVED flow-input values so the editor shows what will
    // actually run. Without this the parameter fields opened blank showing only
    // the flow's defaults as placeholders — and since a blank field means "use
    // the default" on save, editing anything unrelated (a rename, a location
    // change) silently reset a per-configuration value back to the flow default.
    flowInputs: ((c as any).inputs as Record<string, any>) ?? {},
    saveAs: false,
    // Edit mode renders a Name field backed by saveAsName so the config can
    // be renamed in place.
    saveAsName: c.name,
  };
}
